import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import MainLayout from '@/components/layout/MainLayout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Plus, 
  FolderOpen, 
  Users, 
  TrendingUp,
  ArrowRight,
  Calendar,
  MoreHorizontal,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { getStrategyById } from '@/lib/constants';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import FounderWaiting from './FounderWaiting';
import WelcomeScreen from '@/components/onboarding/WelcomeScreen';

const Dashboard = () => {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [isFirstTimeUser, setIsFirstTimeUser] = useState(false);
  const [loading, setLoading] = useState(true);
  const [accessStatus, setAccessStatus] = useState(null);
  const [checkingAccess, setCheckingAccess] = useState(true);

  useEffect(() => {
    checkAccessStatus();
  }, []);

  // Check if user has access (Founder pre-sale gating)
  const checkAccessStatus = async () => {
    try {
      const response = await api.get('/billing/access-status');
      setAccessStatus(response.data);
      
      // If user has access, fetch dashboard stats
      if (response.data.has_access) {
        fetchStats();
      }
    } catch (error) {
      // If error, assume user has access (fail open)
      console.warn('Could not check access status:', error);
      fetchStats();
    } finally {
      setCheckingAccess(false);
    }
  };

  // 🎯 If user doesn't have access yet (Founder waiting), show waiting screen
  if (!checkingAccess && accessStatus && !accessStatus.has_access) {
    return (
      <FounderWaiting 
        accessStartsAt={accessStatus.access_starts_at}
        plan={accessStatus.plan}
        userName={user?.name}
      />
    );
  }

  const fetchStats = async () => {
    try {
      const [funnelsRes, clientsRes] = await Promise.all([
        api.get('/funnels'),
        api.get('/clients')
      ]);

      const funnels = funnelsRes.data || [];
      const clients = clientsRes.data || [];

      // Check if first-time user (no funnels yet)
      setIsFirstTimeUser(funnels.length === 0);

      setStats({
        funnels_count: funnels.length,
        clients_count: clients.length,
        active_funnels: funnels.filter(f => f.status === 'in_progress').length,
        recent_funnels: funnels.slice(0, 5)
      });
    } catch (error) {
      toast.error('Error al cargar estadísticas');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteFunnel = async (funnelId) => {
    try {
      await api.delete(`/funnels/${funnelId}`);
      toast.success('Funnel eliminado');
      fetchStats();
    } catch (error) {
      toast.error('Error al eliminar funnel');
    }
  };

  const handleDuplicateFunnel = async (funnelId) => {
    try {
      await api.post(`/funnels/${funnelId}/duplicate`);
      toast.success('Funnel duplicado');
      fetchStats();
    } catch (error) {
      toast.error('Error al duplicar funnel');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { label: 'Borrador', className: 'bg-muted/50 text-muted-foreground' },
      in_progress: { label: 'En progreso', className: 'bg-amber-500/10 text-amber-600' },
      active: { label: 'Activo', className: 'bg-emerald-500/10 text-emerald-600' },
      completed: { label: 'Completado', className: 'bg-brand-500/10 text-brand-500' },
    };
    const config = statusConfig[status] || statusConfig.draft;
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  const statCards = [
    {
      id: 'clients',
      label: 'Total Clientes',
      value: stats?.clients_count || 0,
      icon: Users,
      color: 'text-brand-500',
      bgColor: 'bg-brand-500/10',
    },
    {
      id: 'funnels',
      label: 'Total Funnels',
      value: stats?.funnels_count || 0,
      icon: FolderOpen,
      color: 'text-emerald-500',
      bgColor: 'bg-emerald-500/10',
    },
    {
      id: 'active',
      label: 'Funnels Activos',
      value: stats?.active_funnels || 0,
      icon: TrendingUp,
      color: 'text-amber-500',
      bgColor: 'bg-amber-500/10',
    },
  ];

  // 🎉 Show welcome screen for first-time users (no funnels)
  if (!loading && !checkingAccess && isFirstTimeUser) {
    return (
      <MainLayout>
        <WelcomeScreen userName={user?.name} />
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6 lg:space-y-8" data-testid="dashboard-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-foreground">Dashboard</h1>
            <p className="text-muted-foreground text-sm sm:text-base mt-1">Gestiona tus funnels de marketing</p>
          </div>
          <Button
            onClick={() => navigate('/funnel/new')}
            className="bg-brand-500 hover:bg-brand-600 text-white shadow-glow-brand w-full sm:w-auto"
            data-testid="create-funnel-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Funnel
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
          {statCards.map((stat) => (
            <div
              key={stat.id}
              className="bg-card border border-border rounded-xl lg:rounded-2xl p-4 lg:p-6 card-hover shadow-sm"
              data-testid={`stat-card-${stat.id}`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-muted-foreground text-xs sm:text-sm">{stat.label}</p>
                  <p className="font-display text-2xl lg:text-3xl font-bold text-foreground mt-1">
                    {loading ? '...' : stat.value}
                  </p>
                </div>
                <div className={`w-10 h-10 lg:w-12 lg:h-12 rounded-xl ${stat.bgColor} flex items-center justify-center`}>
                  <stat.icon className={`w-5 h-5 lg:w-6 lg:h-6 ${stat.color}`} />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Recent Funnels */}
        <div className="bg-card border border-border rounded-xl lg:rounded-2xl overflow-hidden shadow-sm">
          <div className="p-4 lg:p-6 border-b border-border flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <h2 className="font-display text-lg lg:text-xl font-semibold text-foreground">Funnels Recientes</h2>
            <Button
              variant="ghost"
              onClick={() => navigate('/funnels')}
              className="text-muted-foreground hover:text-brand-500 w-full sm:w-auto justify-center sm:justify-start"
            >
              Ver todos
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>

          {loading ? (
            <div className="p-8 text-center text-muted-foreground">Cargando...</div>
          ) : stats?.recent_funnels?.length === 0 ? (
            <div className="p-12 text-center">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-brand-100 to-violet-100 dark:from-brand-900/30 dark:to-violet-900/30 flex items-center justify-center mx-auto mb-4">
                <FolderOpen className="w-8 h-8 text-brand-500" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">No hay funnels aún</h3>
              <p className="text-muted-foreground mb-2 max-w-sm mx-auto">
                Crea tu primer funnel para empezar a generar copy y creatividades con IA
              </p>
              <p className="text-sm text-brand-500 font-medium mb-6">
                Tu primer funnel completo en ~5 minutos
              </p>
              <Button
                onClick={() => navigate('/funnel/new')}
                className="bg-gradient-to-r from-brand-500 to-violet-500 hover:from-brand-600 hover:to-violet-600 text-white shadow-lg"
              >
                <Plus className="w-4 h-4 mr-2" />
                Crear Primer Funnel
              </Button>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {stats?.recent_funnels?.map((funnel) => {
                const strategy = getStrategyById(funnel.strategy);
                return (
                  <div
                    key={funnel.id}
                    className="p-3 sm:p-4 hover:bg-periwinkle-100/30 transition-colors cursor-pointer group"
                    onClick={() => navigate(`/funnel/${funnel.id}`)}
                    data-testid={`funnel-row-${funnel.id}`}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div className="flex items-center gap-3 sm:gap-4">
                        <div
                          className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: `${strategy?.color}20` }}
                        >
                          <span style={{ color: strategy?.color }} className="text-sm sm:text-base font-medium">
                            {strategy?.name?.charAt(0) || 'F'}
                          </span>
                        </div>
                        <div className="min-w-0 flex-1">
                          <h3 className="font-medium text-foreground group-hover:text-brand-500 transition-colors truncate text-sm sm:text-base">
                            {funnel.name}
                          </h3>
                          <p className="text-xs sm:text-sm text-muted-foreground truncate">
                            {funnel.client_name} • {strategy?.name || funnel.strategy}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between sm:justify-end gap-2 sm:gap-4 ml-12 sm:ml-0">
                        <div className="flex items-center gap-2">
                          {getStatusBadge(funnel.status)}
                          <div className="hidden sm:flex items-center gap-2 text-muted-foreground text-sm">
                            <Calendar className="w-4 h-4" />
                            {new Date(funnel.created_at).toLocaleDateString('es-ES')}
                          </div>
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="bg-card border-border">
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/funnel/${funnel.id}`);
                              }}
                            >
                              Editar
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDuplicateFunnel(funnel.id);
                              }}
                            >
                              Duplicar
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteFunnel(funnel.id);
                              }}
                              className="text-red-400 focus:text-red-400"
                            >
                              Eliminar
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
};

export default Dashboard;
